import type {
  BaseIntegrationsRepository,
  IngestServerOption,
  IntegrationUser,
  ViewersResponse,
  AllUsersResponse,
} from "./BaseIntegrationsRepository.interface";
import { localApiBase } from "../../../lib/localApi";

class ApiIntegrationsRepository implements BaseIntegrationsRepository {
  private get Domain(): string {
    return localApiBase();
  }

  async getIngest(platform: string): Promise<IngestServerOption[] | null> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/ingest/${platform}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return null;
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("❌ Error fetching ingest servers:", error);
      return null;
    }
  }

  async getUserIntegrations(platform: string): Promise<IntegrationUser | null> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/user/${platform}`);
      if (!response.ok) return null;
      const data = await response.json();
      if (data?.user?.avatar) {
        data.user.avatar = this.resolveAvatarUrl(data.user.avatar);
      }
      return data;
    } catch (error) {
      console.error("❌ Error fetching user integrations:", error);
      return null;
    }
  }

  async getAllUserIntegrations(): Promise<AllUsersResponse | null> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/users/all`);
      if (!response.ok) return null;
      const data = await response.json();
      if (data?.data) {
        for (const key of Object.keys(data.data)) {
          const integration = data.data[key];
          if (integration?.user?.avatar) {
            integration.user.avatar = this.resolveAvatarUrl(integration.user.avatar);
          }
        }
      }
      return data;
    } catch (error) {
      console.error("❌ Error fetching all user integrations:", error);
      return null;
    }
  }

  private resolveAvatarUrl(avatar: string): string {
    // Already a full URL (http/https) — use as-is
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
      return avatar;
    }
    // Relative API path from backend — prepend domain
    if (avatar.startsWith("/api/")) {
      return `${this.Domain}${avatar}`;
    }
    // Raw local file path (Windows or Unix) — route through files API
    if (avatar.match(/^[A-Z]:\\/) || avatar.startsWith("/")) {
      return `${this.Domain}/api/files?file_path=${encodeURIComponent(avatar)}`;
    }
    return avatar;
  }

  async authIntegration(platform: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/auth/${platform}`, {
        headers: { "Content-Type": "application/json" },
      });
      return response.ok;
    } catch (error) {
      console.error(`❌ Error authenticating ${platform} integration:`, error);
      return false;
    }
  }

  async logoutIntegration(platform: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/logout/${platform}`, {
        method: "POST",
      });
      return response.ok;
    } catch (error) {
      console.error(`❌ Error logging out ${platform} integration:`, error);
      return false;
    }
  }

  async getViewers(): Promise<ViewersResponse | null> {
    try {
      const response = await fetch(`${this.Domain}/api/integrations/viewers`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return null;
      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`❌ Error fetching viewers:`, error);
      return null;
    }
  }
}

export default ApiIntegrationsRepository;
